import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as DeletedAccountActions from './deleted-account.actions';
import { Store } from '@ngrx/store';
import { catchError, concatMap, filter, map, withLatestFrom } from 'rxjs/operators';
import { combineLatest, of } from 'rxjs';
import { globalFailure } from './../../../app/@store/common.actions';
import { getAccountListConfig, getAccountsMeta } from './../../../app/@store/deleted-account/deleted-account.selectors';
import { IMeta } from './../../../app/@store/request-options.interface';
import { PublicPartnerApiService } from './../../../app/@shared/services/publicpartnerapi.service';
import { loginSuccess } from '../auth/auth.actions';
import { HttpParams } from '@angular/common/http';
import { AccountsResponse } from './../../../app/@shared/models/accounts_response.model';
import * as _ from 'lodash';
import { deleteAccountSuccess } from '../account/account.actions';

@Injectable()
export class DeletedAccountEffects {
  constructor(private readonly actions$: Actions, private readonly store: Store, private readonly api: PublicPartnerApiService) { }

  loadContracts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeletedAccountActions.loadContracts),
      filter(action => action.fromBatch !== true),
      withLatestFrom(this.store.select(getAccountListConfig), this.store.select(getAccountsMeta)),
      concatMap(
        ([action, config, _meta]) =>
          this.api.get<AccountsResponse>('/partners/##CURRENT_PARTNER_UUID##/contracts', new HttpParams({ fromObject: { ..._.omitBy(config, c => c === undefined) } })).pipe(
            map(result => {
              const total = result.contracts[0]?.total_row_count || _meta.total || 0;

              const meta: IMeta = {
                count: result.contracts.length,
                limit: _.get(config, 'limit', 10),
                page: 0,
                total
              };

              return DeletedAccountActions.loadContractsSuccess({
                data: result.contracts || [],
                meta
              });
            }),
            catchError(error => of(DeletedAccountActions.loadContractsFailure({ error }), globalFailure({ error })))
          )
      )
    )
  );

  loadContractsTotal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeletedAccountActions.loadContractsTotal, loginSuccess),
      concatMap(action => {
        return this.api.get<AccountsResponse>('/partners/##CURRENT_PARTNER_UUID##/contracts', { fields: ['object_uuid'], filter: 'deleted=true', limit: '1', page: '0' }).pipe(
          map((result) => {
            const total = result.contracts[0]?.total_row_count || 0;

            return DeletedAccountActions.loadContractsTotalSuccess({
              total
            });
          }),
          catchError(error => of(DeletedAccountActions.loadContractsTotalFailure({ error }), globalFailure({ error })))
        );
      })
    )
  );

  loadContractsNext$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeletedAccountActions.loadContractsNext),
      withLatestFrom(combineLatest([this.store.select(getAccountsMeta), this.store.select(getAccountListConfig)])),
      concatMap(([action, [_meta, _listConfig]]) => {
        const nextPage = _.get(_meta, 'page', 0) + 1;
        const totalPages = Math.ceil(_.get(_meta, 'total', 0) / _.get(_meta, 'limit', 10));

        const config = { ..._.omitBy(_listConfig, c => c === undefined) };

        if (nextPage <= totalPages) {
          config.page = nextPage;
        }

        return this.api.get<AccountsResponse>('/partners/##CURRENT_PARTNER_UUID##/contracts', new HttpParams({ fromObject: { ...config } })).pipe(
          map((result) => {
            const total = result.contracts[0]?.total_row_count || _meta.total || 0;

            const meta: IMeta = {
              count: result.contracts.length,
              limit: config.limit || 10,
              page: config.page || 0,
              total
            };

            return DeletedAccountActions.loadContractsNextSuccess({
              data: result.contracts || [],
              meta
            });
          }),
          catchError(error => of(DeletedAccountActions.loadContractsNextFailure({ error }), globalFailure({ error })))
        );
      })
    )
  );

  loadAfterDeleteSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteAccountSuccess),
      map(action => DeletedAccountActions.loadContracts({}))
    )
  );

  loadTotalAfterDeleteSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteAccountSuccess),
      map(action => DeletedAccountActions.loadContractsTotal())
    )
  );

}
